import React, { useCallback, useEffect, useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import ClientTableTopHeader from './ClientTableTopHeader';
import {
  IActivity,
  clearDraftActivity,
  fetchActivitiesRequest,
} from '../../../redux/activity/activitySlice';
import { columns, activeClientColumns } from './ActivityTableColumns';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { PaginationModelChangeProps } from '../../ClientTable/ClientTable';
import { ActivityTypeWithAllEnum } from '../../../types/activity';
import CustomNoRowsOverlay from '../../Table/CustomNoRowsOverlay';
import LoadingSkeleton from './LoadingSkeleton';
import CustomModal from '../../Common/CustomModal';
import MagicSuggestionCard from '../../CreateActivityContainer/MagicSuggestion/MagicSuggestionCard';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';

interface IClientActivityTableProps {
  handleNewActivityCreation: () => void;
}

interface CellClickProps {
  field: string;
  row: IActivity;
}

const StyledBox = styled(Box)`
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  box-shadow: 0px 4px 40px 0px rgba(255, 221, 200, 0.25);
  border-radius: 10px;
  border: 1px solid var(--Brand-Seashell, #fff);
  background: var(--primary-colors-ffffff, #fff);
`;

const StyledDataGridBox = styled(Box)`
  background-color: #fff;
  height: calc(100vh - 310px);
  width: 100%;
`;

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  borderRight: 'none',
  borderLeft: 'none',
  borderBottom: 'none',
  borderColor: '#7CAEBE',
  borderRadius: '0px',
  height: 'calc(100vh - 310px)',
  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
    paddingLeft: '30px',
    borderColor: theme.palette.primary.light,
  },

  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },

  '& .MuiDataGrid-columnHeaderTitle': {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },

  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none',
  },

  '& .MuiDataGrid-columnHeaderTitleContainer': {
    paddingLeft: '20px',
    border: 'none',
  },

  '& .MuiDataGrid-columnHeaders': {
    borderColor: '#7CAEBE',
  },

  '& .MuiDataGrid-footerContainer': {
    borderColor: '#7CAEBE',
  },
  '& .MuiTablePagination-root button': {
    mt: '4px',
    color: '#6CA3AC' /* Change text color */,
    '&:disabled': {
      opacity: 0.5,
    },
  },
  '& .MuiTablePagination-input': {
    color: 'primary.main',
    fontFamily: 'Satoshi',
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: '18px',
    mt: '4px',
    ml: 0,
    pl: 0,
  },
  '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
    color: '#7CAEBE',
    fontFamily: 'Satoshi',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '18px',
  },
  '& .MuiTablePagination-displayedRows': {
    color: 'primary.main',
    fontFamily: 'Satoshi',
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: '18px',
    mt: '4px',
  },
  '& .MuiDataGrid-virtualScroller': {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    overflowX: 'hidden',
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f5f5f5',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '20px',
      border: '3px solid #f5f5f5',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#888',
    },
  },
}));

function ClientActivityTable({ handleNewActivityCreation }: IClientActivityTableProps) {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useAppDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const clientActivities = useAppSelector((state) => state.activity.activities ?? []);
  const totalActivities = useAppSelector((state) => state.activity.totalActivityCount ?? 0);
  const fetchActivitiesLoading = useAppSelector((state) => state.activity.fetchActivitiesLoading);
  const [searchText, setSearchText] = useState('');
  const [activityType, setActivityType] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IActivity>({
    name: '',
    objective: '',
  });
  const selectedClient = useAppSelector((state) => state.client.selectedClient);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const handleOnPaginationModelChange = (event: PaginationModelChangeProps) => {
    const newPage = event.page;
    const newPageSize = event.pageSize;
    setPageSize(newPageSize);
    setPage(newPage);
    if (!id || !coachId) return;
    if (activityType && activityType !== ActivityTypeWithAllEnum.ALL) {
      dispatch(
        fetchActivitiesRequest({
          clientId: id,
          coachId,
          skip: newPage * newPageSize,
          take: newPageSize,
          filterText: searchText,
          schedule: activityType as ActivityTypeWithAllEnum,
        })
      );
    } else {
      dispatch(
        fetchActivitiesRequest({
          clientId: id,
          coachId,
          skip: newPage * newPageSize,
          take: newPageSize,
          filterText: searchText,
        })
      );
    }
  };

  useEffect(() => {
    if (id && coachId) {
      dispatch(
        fetchActivitiesRequest({
          clientId: id,
          coachId,
          skip: page * pageSize,
          take: pageSize,
          filterText: '',
        })
      );
    }
  }, [id, coachId]);

  const debouncedFetchActivities = useCallback(
    debounce(({ searchTerm, activity }: { searchTerm: string; activity: string }) => {
      if (!id || !coachId) return;
      if (activity && activity !== ActivityTypeWithAllEnum.ALL) {
        dispatch(
          fetchActivitiesRequest({
            clientId: id,
            coachId,
            skip: page * pageSize,
            take: pageSize,
            filterText: searchTerm,
            schedule: activity as ActivityTypeWithAllEnum,
          })
        );
      } else {
        dispatch(
          fetchActivitiesRequest({
            clientId: id,
            coachId,
            skip: page * pageSize,
            take: pageSize,
            filterText: searchTerm,
          })
        );
      }
    }, 1000),
    [id, coachId, page, pageSize, dispatch]
  );

  useEffect(
    () => () => {
      debouncedFetchActivities.cancel();
    },
    [debouncedFetchActivities]
  );

  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    debouncedFetchActivities({ searchTerm: event.target.value, activity: activityType });
  };

  const activityFilterHandler = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    if (Object.values(ActivityTypeWithAllEnum).includes(value as ActivityTypeWithAllEnum)) {
      const selectedActivityType = value as ActivityTypeWithAllEnum;
      setActivityType(selectedActivityType);
      debouncedFetchActivities({ searchTerm: searchText, activity: selectedActivityType });
    }
  };

  // eslint-disable-next-line no-undef
  const handleOnCellClick = (params: CellClickProps) => {
    if (params.field !== 'option' && params.field !== 'status') {
      setModalOpen(true);
      setSelectedRow(params.row);
    }
  };

  useEffect(
    () => () => {
      dispatch(clearDraftActivity());
    },
    []
  );

  return (
    <StyledBox>
      <ClientTableTopHeader
        handleNewActivityCreation={handleNewActivityCreation}
        searchHandler={searchHandler}
        searchText={searchText}
        activityFilterHandler={activityFilterHandler}
        activityType={activityType as ActivityTypeWithAllEnum}
      />
      <StyledDataGridBox>
        <StyledDataGrid
          rows={!fetchActivitiesLoading ? clientActivities : []}
          columns={
            selectedClient?.isActive
              ? (activeClientColumns as GridColDef[])
              : (columns as GridColDef[])
          }
          paginationMode="server"
          columnVisibilityModel={{
            id: false,
            option: checkPermissions(UserPermissionType.ACTIVITY_MODULE_EDIT_ACTIVITY),
          }}
          disableRowSelectionOnClick
          loading={fetchActivitiesLoading}
          rowCount={totalActivities}
          pagination
          initialState={{
            pagination: {
              paginationModel: {
                pageSize,
                page,
              },
            },
          }}
          pageSizeOptions={[1, 5, 10, 15]}
          onPaginationModelChange={handleOnPaginationModelChange}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: LoadingSkeleton,
          }}
          onCellClick={handleOnCellClick}
          sx={{
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
          }}
        />
      </StyledDataGridBox>
      <CustomModal isOpen={isModalOpen} xs={5}>
        <MagicSuggestionCard
          name={selectedRow.name}
          activitySteps={selectedRow.ActivityStep || []}
          objective={selectedRow.objective}
          closeModal={() => setModalOpen(false)}
        />
      </CustomModal>
    </StyledBox>
  );
}

export default ClientActivityTable;
